import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Mail, Phone, MapPin, FileText, CheckCircle, X } from 'lucide-react';
import { getServiceLineGross, getOfferTotals, getOfferSalePercent, formatSalePercent, shouldShowLinePrices, shouldShowSubtotals, shouldShowTotals, formatOfferTotalText } from '../utils/offerLineDisplay';
import { offerColorStyles, brandButtonProps } from '../utils/offerColor';
import useOfferEngagement from '../hooks/useOfferEngagement';

/**
 * Reusable offer form: fetches offer by offerId+token, shows summary + customer form + confirmation.
 * Used by OfferDisplay (standalone page) and StepperChatbotWidget (inline step).
 */
const OfferFormStep = ({
  offerId,
  token,
  onSuccess,
  onReject,
  showCloseButton = false,
  onClose,
  compact = false,
  showOnlyItems = false,
}) => {
  const { t } = useTranslation();
  const [offer, setOffer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    zip: '',
    country: ''
  });
  const [isSubmittingOffer, setIsSubmittingOffer] = useState(false);
  const [step, setStep] = useState('info'); // 'info' or 'confirmation'
  const [developerChatHref, setDeveloperChatHref] = useState(null);
  const trackEngagement = Boolean(offer && offer.status === null && step === 'info' && !isLoading && !error);
  const readSentinelRef = useOfferEngagement({ offerId, token, active: trackEngagement });

  useEffect(() => {
    if (offerId && token) {
      fetchOffer();
    }
  }, [offerId, token]);

  useEffect(() => {
    if (!offer?.chatbotId) {
      setDeveloperChatHref(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/embed/${offer.chatbotId}/config?t=${Date.now()}`);
        if (!res.ok || cancelled) return;
        const config = await res.json();
        if (cancelled) return;
        if (config.developerMode) {
          setDeveloperChatHref(`${window.location.origin}/embed/${offer.chatbotId}`);
        } else {
          setDeveloperChatHref(null);
        }
      } catch {
        if (!cancelled) setDeveloperChatHref(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [offer]);

  useEffect(() => {
    if (!offer || !offer.customerInfo) return;
    const info = offer.customerInfo;
    if (info.street || info.city || info.zip) {
      setCustomerInfo(prev => ({
        ...prev,
        name: info.name || prev.name,
        email: info.email || prev.email,
        phone: info.phone || prev.phone,
        street: info.street || '',
        city: info.city || '',
        zip: info.zip != null ? String(info.zip) : '',
        country: info.country || ''
      }));
    }
  }, [offer]);

  const fetchOffer = async () => {
    try {
      if (!token) {
        setError(t('offer.invalidToken'));
        setIsLoading(false);
        return;
      }

      const response = await fetch(`/api/offers/${offerId}?token=${token}`);
      const data = await response.json();

      if (response.ok) {
        setOffer(data.offer);
      } else {
        if (response.status === 403) {
          setError(t('offer.invalidToken'));
        } else if (response.status === 410) {
          setError(t('offer.linkExpired'));
        } else {
          setError(data.error || t('offer.offerNotFound'));
        }
      }
    } catch (err) {
      console.error('Error fetching offer:', err);
      setError(t('offer.failedToLoadOffer'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  const validateCustomerInfo = () => {
    return customerInfo.name.trim() &&
      customerInfo.email.trim() &&
      customerInfo.phone.trim() &&
      customerInfo.street.trim() &&
      customerInfo.city.trim() &&
      customerInfo.zip.trim();
  };

  const handleAcceptOffer = async () => {
    if (!validateCustomerInfo()) {
      alert(t('offer.fillRequiredFields'));
      return;
    }

    setIsSubmittingOffer(true);
    try {
      const updateResponse = await fetch(`/api/offers/${offerId}/customer-info`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: customerInfo.name,
          email: customerInfo.email,
          phone: customerInfo.phone,
          street: customerInfo.street,
          city: customerInfo.city,
          zip: customerInfo.zip,
          country: customerInfo.country || undefined
        }),
      });

      if (!updateResponse.ok) {
        throw new Error(t('offer.failedToUpdateCustomerInfo'));
      }

      const acceptResponse = await fetch(`/api/offers/${offerId}/accept`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!acceptResponse.ok) {
        const errorData = await acceptResponse.json();
        throw new Error(errorData.error || t('offer.failedToAcceptOffer'));
      }

      setStep('confirmation');
      onSuccess?.();
    } catch (err) {
      console.error('Error processing offer:', err);
      alert(err.message || t('offer.errorProcessingOffer'));
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  const handleRejectOffer = async () => {
    if (!window.confirm(t('offer.confirmReject'))) return;

    setIsSubmittingOffer(true);
    try {
      const rejectResponse = await fetch(`/api/offers/${offerId}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Customer rejected the offer' }),
      });

      if (!rejectResponse.ok) {
        const errorData = await rejectResponse.json();
        throw new Error(errorData.error || t('offer.failedToAcceptOffer'));
      }

      alert(t('offer.offerRejected'));
      onReject?.();
    } catch (err) {
      console.error('Error rejecting offer:', err);
      alert(err.message || t('offer.errorRejectingOffer'));
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${compact ? 'py-8' : 'min-h-[200px]'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('offer.loadingOffer')}</p>
        </div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className={`flex items-center justify-center p-4 ${compact ? '' : 'min-h-[200px]'}`}>
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('common.error')}</h2>
          <p className="text-gray-600 mb-6">{error || t('offer.offerNotFound')}</p>
          {showCloseButton && onClose && (
            <button
              onClick={onClose}
              className="px-6 py-2 text-white rounded-md"
              {...brandButtonProps(offerColorStyles())}
            >
              {t('common.close')}
            </button>
          )}
        </div>
      </div>
    );
  }

  const theme = offerColorStyles(offer.primaryColor);

  if (offer.status !== null) {
    return (
      <div className={`flex items-center justify-center p-4 ${compact ? '' : 'min-h-[200px]'}`}>
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="h-8 w-8 text-yellow-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('offer.offerAlreadyProcessed')}</h2>
          <p className="text-gray-600 mb-6">
            {t('offer.offerAlreadyProcessedMessage', { status: offer.status === 'pending' ? t('customer.status.accepted') : offer.status })}
          </p>
          {showCloseButton && onClose && (
            <button
              onClick={onClose}
              className="px-6 py-2 text-white rounded-md"
              {...brandButtonProps(theme)}
            >
              {t('common.close')}
            </button>
          )}
        </div>
      </div>
    );
  }

  const { subtotalNet, totalVat, totalGross } = getOfferTotals(offer);
  const salePercent = getOfferSalePercent(offer);
  const totalAmount = formatOfferTotalText(offer) || 'TBD';
  const showLinePrices = shouldShowLinePrices(offer);
  const showTotals = shouldShowTotals(offer);
  const showSubtotals = shouldShowSubtotals(offer);

  const containerClass = compact ? 'p-4' : 'p-6';

  return (
    <div className={containerClass} style={{ '--offer-color': theme.color }}>
      <div className="flex items-center justify-between mb-6">
        <h2 className={`font-bold text-gray-900 flex items-center ${compact ? 'text-xl' : 'text-2xl'}`}>
          <FileText className="h-6 w-6 mr-2" style={{ color: theme.color }} />
          {step === 'info' ? t('offer.acceptYourOffer') : t('offer.offerSentSuccess')}
        </h2>
        {showCloseButton && onClose && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        )}
      </div>

      {developerChatHref && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="mb-2">{t('offer.developerBackToChatbotHint')}</p>
          <a
            href={developerChatHref}
            className="font-medium text-amber-900 underline decoration-amber-700 underline-offset-2 hover:text-amber-950"
          >
            {t('offer.developerBackToChatbot')}
          </a>
        </div>
      )}

      {step === 'info' ? (
        <>
          {/* Offer Summary */}
          <div className="border rounded-lg p-4 mb-6" style={theme.card}>
            {offer.services && offer.services.length > 0 ? (
              <>
                <div className="space-y-2">
                  {offer.services.map((service, index) => (
                    <div key={index} className="bg-white rounded p-3 text-sm flex justify-between items-start gap-4">
                      <div>
                        <div className="font-medium" style={theme.strong}>{service.name}</div>
                        {service.description && (
                          <div className="text-xs mt-1" style={theme.body}>{service.description}</div>
                        )}
                      </div>
                      {showLinePrices && service.price !== undefined && (
                        <div className="font-semibold whitespace-nowrap text-right" style={theme.strong}>
                          <div>{offer.currency || 'EUR'} {getServiceLineGross(service).toFixed(2)}</div>
                          {service.lineNet != null && service.lineVat > 0 && (
                            <div className="text-xs font-normal" style={theme.muted}>
                              {t('offer.lineNetVat', {
                                net: service.lineNet.toFixed(2),
                                vat: service.lineVat.toFixed(2)
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {showTotals && totalAmount !== 'TBD' && (
                  <div className="mt-4 pt-3 border-t space-y-1" style={theme.divider}>
                    {showSubtotals && subtotalNet != null && totalVat != null && totalVat > 0 && (
                      <>
                        <div className="flex justify-between items-center text-sm" style={theme.body}>
                          <span>{t('offer.subtotalNet')}</span>
                          <span>{offer.currency || 'EUR'} {Number(subtotalNet).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm" style={theme.body}>
                          <span>{t('offer.totalVat')}</span>
                          <span>{offer.currency || 'EUR'} {Number(totalVat).toFixed(2)}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold" style={theme.strong}>
                        {offer.displayMode === 'range'
                          ? t('offer.estimatedTotal')
                          : totalVat > 0
                            ? t('offer.totalGross')
                            : t('offer.totalAmount')}
                      </span>
                      <span className="text-lg font-bold" style={theme.strong}>{totalAmount}</span>
                    </div>
                    {salePercent > 0 && (
                      <div className="text-sm font-medium" style={theme.strong}>
                        {t('offer.saleApplied', { percent: formatSalePercent(salePercent) })}
                      </div>
                    )}
                    {offer.validUntil && (
                      <div className="text-xs mt-1" style={theme.body}>
                        {t('offer.validUntil', { date: new Date(offer.validUntil).toLocaleDateString() })}
                      </div>
                    )}
                  </div>
                )}
                {!showTotals && salePercent > 0 && (
                  <div className="mt-3 text-sm font-medium" style={theme.strong}>
                    {t('offer.saleApplied', { percent: formatSalePercent(salePercent) })}
                  </div>
                )}
                {offer.notes && (
                  <div className="mt-3 pt-3 border-t" style={theme.divider}>
                    <div className="text-xs italic whitespace-pre-wrap" style={theme.body}>{offer.notes}</div>
                  </div>
                )}
              </>
            ) : (
              <>
                {!showOnlyItems && (
                  <>
                    <div className="text-sm whitespace-pre-wrap mb-3" style={theme.body}>{offer.offerDetails}</div>
                    {showTotals && totalAmount !== 'TBD' && (
                      <div className="mt-3 pt-3 border-t" style={theme.divider}>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold" style={theme.strong}>
                            {offer.displayMode === 'range' ? t('offer.estimatedTotal') : t('offer.totalAmount')}
                          </span>
                          <span className="text-lg font-bold" style={theme.strong}>{totalAmount}</span>
                        </div>
                        {salePercent > 0 && (
                          <div className="text-sm font-medium mt-1" style={theme.strong}>
                            {t('offer.saleApplied', { percent: formatSalePercent(salePercent) })}
                          </div>
                        )}
                        {offer.validUntil && (
                          <div className="text-xs mt-1" style={theme.body}>
                            {t('offer.validUntil', { date: new Date(offer.validUntil).toLocaleDateString() })}
                          </div>
                        )}
                      </div>
                    )}
                    {!showTotals && salePercent > 0 && (
                      <div className="mt-3 text-sm font-medium" style={theme.strong}>
                        {t('offer.saleApplied', { percent: formatSalePercent(salePercent) })}
                      </div>
                    )}
                    {offer.notes && (
                      <div className="mt-3 pt-3 border-t" style={theme.divider}>
                        <div className="text-xs italic whitespace-pre-wrap" style={theme.body}>{offer.notes}</div>
                      </div>
                    )}
                  </>
                )}
                {showOnlyItems && (
                  <p className="text-sm" style={theme.body}>{t('offer.noServices')}</p>
                )}
              </>
            )}
            <div ref={readSentinelRef} className="h-px w-full" aria-hidden="true" />
          </div>

          {/* Customer Information Form */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('offer.yourInformation')}</h3>
            <p className="text-sm text-gray-600 mb-2">{t('offer.contactInfoHint')}</p>
            <p className="text-sm text-gray-600 mb-4">{t('offer.dataUsageNotice')}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User className="inline h-4 w-4 mr-1" />
                  {t('offer.fullName')}
                </label>
                <input
                  type="text"
                  name="name"
                  value={customerInfo.name}
                  onChange={handleCustomerInfoChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input"
                  placeholder={t('offer.placeholderName')}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="inline h-4 w-4 mr-1" />
                  {t('offer.emailAddress')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={customerInfo.email}
                  onChange={handleCustomerInfoChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input"
                  placeholder={t('offer.placeholderEmail')}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Phone className="inline h-4 w-4 mr-1" />
                  {t('offer.phoneNumber')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={customerInfo.phone}
                  onChange={handleCustomerInfoChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input"
                  placeholder={t('offer.placeholderPhone')}
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="inline h-4 w-4 mr-1" />
                  {t('offer.address')}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-1">
                  <input
                    type="text"
                    name="street"
                    value={customerInfo.street}
                    onChange={handleCustomerInfoChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input md:col-span-3"
                    placeholder={t('offer.placeholderStreet')}
                    required
                  />
                  <input
                    type="text"
                    name="zip"
                    value={customerInfo.zip}
                    onChange={handleCustomerInfoChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input"
                    placeholder={t('offer.placeholderZip')}
                    required
                  />
                  <input
                    type="text"
                    name="city"
                    value={customerInfo.city}
                    onChange={handleCustomerInfoChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input md:col-span-2"
                    placeholder={t('offer.placeholderCity')}
                    required
                  />
                  <input
                    type="text"
                    name="country"
                    value={customerInfo.country}
                    onChange={handleCustomerInfoChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input md:col-span-3"
                    placeholder={t('offer.placeholderCountry')}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={handleRejectOffer}
                disabled={isSubmittingOffer}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmittingOffer ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                    {t('offer.processing')}
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4 mr-2" />
                    {t('offer.rejectOffer')}
                  </>
                )}
              </button>
              <button
                onClick={handleAcceptOffer}
                disabled={!validateCustomerInfo() || isSubmittingOffer}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmittingOffer ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('offer.processing')}
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {t('offer.acceptOffer')}
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      ) : (
        /* Confirmation Step */
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('offer.offerSentSuccess')}</h3>
          <p className="text-gray-600 mb-6 whitespace-pre-line text-left max-w-lg mx-auto">
            {t('offer.submissionNotice', { email: customerInfo.email })}
          </p>
          {showCloseButton && onClose && (
            <button
              onClick={onClose}
              className="px-6 py-2 text-white rounded-md"
              {...brandButtonProps(theme)}
            >
              {t('common.close')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default OfferFormStep;
